import React, { useState, useEffect } from 'react';
import { Form, Button, Card, ListGroup, Alert, Row, Col, InputGroup, Modal } from 'react-bootstrap';
import axios from 'axios';
import { CreditCardFill, PlusCircle, Trash, Pencil } from 'react-bootstrap-icons';
import IconPicker from '../../../shared/components/IconPicker';
import { renderIcon } from '../../../shared/utils/iconUtils'; // Import the shared icon utility

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const PaymentType = {
    CREDIT_CARD: "Credit Card",
    DEBIT_CARD: "Debit Card",
    ONLINE_BANKING: "Online Banking",
    CASH: "Cash",
    OTHER: "Other"
};

function PaymentSourceManager({ profileId }) {
    const [paymentSources, setPaymentSources] = useState([]);
    const [newPaymentSource, setNewPaymentSource] = useState({
        payment_type: PaymentType.OTHER,
        source_name: '',
        note: '',
        icon: null
    });
    const [editingSource, setEditingSource] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Icon Picker States
    const [showIconPickerModal, setShowIconPickerModal] = useState(false);
    const [selectedIcon, setSelectedIcon] = useState(null);
    const [editingIcon, setEditingIcon] = useState(null);
    const [iconPickerContext, setIconPickerContext] = useState('new'); // 'new' or 'edit'

    useEffect(() => {
        if (profileId) {
            fetchPaymentSources();
        }
    }, [profileId]);

    const fetchPaymentSources = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/payment_sources/profiles/${profileId}/payment_sources`);
            const sources = response.data.map(source => {
                try {
                    const icon = JSON.parse(source.icon);
                    return { ...source, icon };
                } catch (e) {
                    return { ...source, icon: { type: 'emoji', value: source.icon } }; // Fallback for old string icons
                }
            });
            setPaymentSources(sources);
        } catch (err) {
            console.error("Error fetching payment sources:", err);
            setError("Failed to fetch payment sources.");
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (editingSource) {
            setEditingSource(prevState => ({ ...prevState, [name]: value }));
        } else {
            setNewPaymentSource(prevState => ({ ...prevState, [name]: value }));
        }
    };

    const handleIconSelect = (icon) => {
        if (iconPickerContext === 'new') {
            setSelectedIcon(icon);
            setNewPaymentSource(prevState => ({ ...prevState, icon: icon }));
        } else {
            setEditingIcon(icon);
            setEditingSource(prevState => ({ ...prevState, icon: icon }));
        }
        setShowIconPickerModal(false);
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!newPaymentSource.source_name) {
            setError("Payment Source Name is required.");
            return;
        }

        try {
            await axios.post(`${API_BASE_URL}/api/payment_sources`, {
                ...newPaymentSource,
                profile_id: profileId,
                icon: JSON.stringify(selectedIcon)
            });
            setSuccess("Payment source added successfully!");
            setNewPaymentSource({ payment_type: PaymentType.OTHER, source_name: '', note: '', icon: null });
            setSelectedIcon(null);
            fetchPaymentSources();
        } catch (err) {
            console.error("Error adding payment source:", err);
            setError(err.response?.data?.detail || "Failed to add payment source.");
        }
    };

    const handleEdit = (source) => {
        setEditingSource(source);
        setEditingIcon(source.icon);
        setShowEditModal(true);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!editingSource.source_name) {
            setError("Payment Source Name is required.");
            return;
        }

        try {
            await axios.put(`${API_BASE_URL}/api/payment_sources/${editingSource.id}`, {
                ...editingSource,
                icon: JSON.stringify(editingIcon)
            });
            setSuccess("Payment source updated successfully!");
            setShowEditModal(false);
            setEditingSource(null);
            fetchPaymentSources();
        } catch (err) {
            console.error("Error updating payment source:", err);
            setError(err.response?.data?.detail || "Failed to update payment source.");
        }
    };

    const handleDelete = async (id) => {
        setError(null);
        setSuccess(null);
        if (window.confirm("Are you sure you want to delete this payment source?")) {
            try {
                await axios.delete(`${API_BASE_URL}/api/payment_sources/${id}`);
                setSuccess("Payment source deleted successfully!");
                fetchPaymentSources();
            } catch (err) {
                console.error("Error deleting payment source:", err);
                setError(err.response?.data?.detail || "Failed to delete payment source.");
            }
        }
    };

    return (
        <Card className="mb-4">
            <Card.Header className="bg-primary text-white d-flex align-items-center">
                <CreditCardFill className="me-2" />
                <h5 className="mb-0">Manage Payment Sources</h5>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <Form onSubmit={handleSubmit} className="mb-4">
                    <Row className="g-3">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Payment Type</Form.Label>
                                <Form.Select name="payment_type" value={newPaymentSource.payment_type} onChange={handleChange}>
                                    {Object.values(PaymentType).map(type => (<option key={type} value={type}>{type}</option>))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Payment Source Name</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    name="source_name" 
                                    value={newPaymentSource.source_name} 
                                    onChange={handleChange} 
                                    placeholder="e.g., Visa ending in 1234, My Bank Account" 
                                    required 
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Note (Optional)</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    name="note" 
                                    value={newPaymentSource.note} 
                                    onChange={handleChange} 
                                    placeholder="Any additional notes" 
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Icon</Form.Label>
                                <Button 
                                    variant="outline-secondary" 
                                    className="w-100 d-flex align-items-center justify-content-center"
                                    onClick={() => { setIconPickerContext('new'); setShowIconPickerModal(true); }}
                                    style={{ height: '38px' }}
                                >
                                    {renderIcon(selectedIcon) || '🏦'}
                                </Button>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Button variant="primary" type="submit" className="mt-3">
                        <PlusCircle className="me-2" />Add Payment Source
                    </Button>
                </Form>

                <h5 className="mt-4">Existing Payment Sources</h5>
                {paymentSources.length === 0 ? (
                    <Alert variant="info" className="text-center">
                        No payment sources added yet. Use the form above to add your first payment source.
                    </Alert>
                ) : (
                    <div className="payment-sources-grid">
                        {paymentSources.map(source => (
                            <Card key={source.id} className="payment-source-card">
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div className="d-flex align-items-center">
                                            {renderIcon(source.icon)}
                                            <div>
                                                <h6 className="mb-1">{source.source_name}</h6>
                                                <small className="text-muted">{source.payment_type}</small>
                                            </div>
                                        </div>
                                        <div className="d-flex gap-1">
                                            <Button variant="outline-primary" size="sm" onClick={() => handleEdit(source)}>
                                                <Pencil size={14} />
                                            </Button>
                                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(source.id)}>
                                                <Trash size={14} />
                                            </Button>
                                        </div>
                                    </div>
                                    {source.note && (
                                        <div className="payment-source-note">
                                            <small className="text-secondary">📝 {source.note}</small>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        ))}
                    </div>
                )}
            </Card.Body>

            <IconPicker show={showIconPickerModal} handleClose={() => setShowIconPickerModal(false)} onSelectIcon={handleIconSelect} />

            {editingSource && (
                <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Payment Source</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form onSubmit={handleUpdate}>
                            <Form.Group className="mb-3">
                                <Form.Label>Payment Type</Form.Label>
                                <Form.Control as="select" name="payment_type" value={editingSource.payment_type} onChange={handleChange}>
                                    {Object.values(PaymentType).map(type => (<option key={type} value={type}>{type}</option>))}
                                </Form.Control>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Source Name</Form.Label>
                                <InputGroup>
                                    <Form.Control type="text" name="source_name" value={editingSource.source_name} onChange={handleChange} required />
                                    <Button variant="outline-secondary" onClick={() => { setIconPickerContext('edit'); setShowIconPickerModal(true); }}>
                                        {renderIcon(editingIcon) || 'Icon'}
                                    </Button>
                                </InputGroup>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Note</Form.Label>
                                <Form.Control as="textarea" name="note" value={editingSource.note} onChange={handleChange} rows={3} />
                            </Form.Group>
                            <Button variant="primary" type="submit">Save Changes</Button>
                        </Form>
                    </Modal.Body>
                </Modal>
            )}
        </Card>
    );
}

export default PaymentSourceManager;
